import {
  LEADERBOARD_LIMIT,
  getLeaderboardWindowStart,
  type LeaderboardMetric,
  type LeaderboardPeriod
} from "../game/leaderboard";
import {
  getProfileNameValidationError,
  sanitizeProfileName,
  sanitizeProfileNameForSave
} from "../game/profileStorage";
import {
  ensureAnonymousSession,
  getAnonymousAuthErrorMessage,
  getSupabaseClient as loadSupabaseClient,
  invokeSupabaseFunction,
  supabaseConfigError
} from "./supabase";
import { runtimeEnv } from "./runtimeEnv";
import {
  GAME_RULES_VERSION,
  GAME_RULES_VERSION_MISMATCH_ERROR,
  isGameRulesVersionMismatchError,
  sanitizeGameRulesVersion
} from "../../../../shared/gameRules.ts";

const LEADERBOARD_TABLE = "leaderboard_entries";
const LEADERBOARD_SELECT = "id, player_id, initials, metric_type, metric_value, created_at";
const LEADERBOARD_METRICS: readonly LeaderboardMetricType[] = ["score", "round", "flow"];
const LEADERBOARD_DEBUG_ENABLED = runtimeEnv.DEV === true;
const FULL_LEADERBOARD_PAGE_SIZE = 100;
const DEFAULT_LEADERBOARD_PLAYER_NAME = "Anonymous";

export type LeaderboardMetricType = LeaderboardMetric;

export type LeaderboardEntry = {
  id: string;
  userId: string;
  playerName: string;
  metricType: LeaderboardMetricType;
  metricValue: number;
  createdAt: string;
};

export type RankedLeaderboardEntry = LeaderboardEntry & {
  rank: number;
};

export type LeaderboardSnapshot = {
  entries: LeaderboardEntry[];
  highlightedEntry: RankedLeaderboardEntry | null;
};

export type SubmitLeaderboardEntryInput = {
  playerName: string;
  metricType: LeaderboardMetricType;
  metricValue: number;
};

export type VerifiedRunAction = {
  handIndex: number;
  boardIndex: number;
  playedAt: number;
};

export type VerifiedRunPauseWindow = {
  startedAt: number;
  endedAt: number;
};

export type StartLeaderboardRunInput = {
  appVersion: string;
  gameRulesVersion: string;
  guidedRun: boolean;
};

export type StartLeaderboardRunResult = {
  runId: string;
  runSeed: string;
  startedAt: number;
};

export type SubmitLeaderboardRunInput = {
  appVersion: string;
  actions: VerifiedRunAction[];
  completedAt: number;
  gameRulesVersion: string;
  playerName: string;
  runId: string;
};

export type SubmitLeaderboardRunResult = {
  accepted: boolean;
  entries: LeaderboardEntry[];
  entryIds: string[];
  flaggedReason: string | null;
};

type LeaderboardRow = {
  id: unknown;
  player_id: unknown;
  initials: unknown;
  metric_type: unknown;
  metric_value: unknown;
  created_at: unknown;
};

async function requireSupabaseClient() {
  const client = await loadSupabaseClient();
  if (client === null) {
    throw new Error(supabaseConfigError ?? "Leaderboard unavailable.");
  }

  return client;
}

function logLeaderboardDebug(message: string, details?: Record<string, unknown>) {
  if (!LEADERBOARD_DEBUG_ENABLED) {
    return;
  }

  if (details === undefined) {
    console.info(`[leaderboard] ${message}`);
    return;
  }

  console.info(`[leaderboard] ${message}`, details);
}

function isLeaderboardMetricType(value: unknown): value is LeaderboardMetricType {
  return typeof value === "string" && LEADERBOARD_METRICS.includes(value as LeaderboardMetricType);
}

function sanitizeMetricValue(value: unknown): number | null {
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      return null;
    }

    return Math.max(0, Math.trunc(value));
  }

  if (typeof value === "string" && value.trim().length > 0) {
    const parsedValue = Number(value);
    if (Number.isFinite(parsedValue)) {
      return Math.max(0, Math.trunc(parsedValue));
    }
  }

  return null;
}

function normalizeMetricValue(value: number): number {
  if (!Number.isFinite(value)) {
    throw new Error("Leaderboard save failed: metric value was invalid.");
  }

  return Math.max(0, Math.trunc(value));
}

function normalizeTimestamp(value: unknown): number {
  const nextTimestamp = normalizeMetricValue(
    typeof value === "string" ? Number(value) : (value as number)
  );
  if (nextTimestamp <= 0) {
    throw new Error("Leaderboard timing metadata was invalid.");
  }

  return nextTimestamp;
}

function normalizeVerifiedRunPauseWindow(
  pauseWindow: VerifiedRunPauseWindow
): VerifiedRunPauseWindow | null {
  const startedAt = normalizeTimestamp(pauseWindow.startedAt);
  const endedAt = normalizeTimestamp(pauseWindow.endedAt);
  if (endedAt <= startedAt) {
    return null;
  }

  return {
    startedAt,
    endedAt
  };
}

function mergeVerifiedRunPauseWindows(
  pauseWindows: readonly VerifiedRunPauseWindow[]
): VerifiedRunPauseWindow[] {
  const normalizedPauseWindows = pauseWindows
    .map(normalizeVerifiedRunPauseWindow)
    .filter((pauseWindow): pauseWindow is VerifiedRunPauseWindow => pauseWindow !== null)
    .sort(
      (left, right) =>
        left.startedAt - right.startedAt || left.endedAt - right.endedAt
    );

  const mergedPauseWindows: VerifiedRunPauseWindow[] = [];

  normalizedPauseWindows.forEach((pauseWindow) => {
    const previousPauseWindow = mergedPauseWindows[mergedPauseWindows.length - 1];
    if (
      previousPauseWindow === undefined ||
      pauseWindow.startedAt > previousPauseWindow.endedAt
    ) {
      mergedPauseWindows.push({ ...pauseWindow });
      return;
    }

    previousPauseWindow.endedAt = Math.max(previousPauseWindow.endedAt, pauseWindow.endedAt);
  });

  return mergedPauseWindows;
}

function getPausedDurationBeforeTimestamp(
  timestamp: number,
  pauseWindows: readonly VerifiedRunPauseWindow[]
): number {
  return pauseWindows.reduce((totalDuration, pauseWindow) => {
    if (pauseWindow.endedAt > timestamp) {
      return totalDuration;
    }

    return totalDuration + (pauseWindow.endedAt - pauseWindow.startedAt);
  }, 0);
}

export function applyPausedTimeWindowsToRunProof(
  actions: readonly VerifiedRunAction[],
  completedAt: number,
  pauseWindows: readonly VerifiedRunPauseWindow[]
): {
  actions: VerifiedRunAction[];
  completedAt: number;
} {
  const mergedPauseWindows = mergeVerifiedRunPauseWindows(pauseWindows);
  const normalizedActions = actions.map((action) => {
    const playedAt = normalizeTimestamp(action.playedAt);
    return {
      ...action,
      playedAt: playedAt - getPausedDurationBeforeTimestamp(playedAt, mergedPauseWindows)
    };
  });

  const normalizedCompletedAt = normalizeTimestamp(completedAt);

  return {
    actions: normalizedActions,
    completedAt:
      normalizedCompletedAt -
      getPausedDurationBeforeTimestamp(normalizedCompletedAt, mergedPauseWindows)
  };
}

export function alignRunProofToServerTimeline(
  actions: readonly VerifiedRunAction[],
  completedAt: number,
  pauseWindows: readonly VerifiedRunPauseWindow[],
  clientStartedAt: number,
  serverStartedAt: number
): {
  actions: VerifiedRunAction[];
  completedAt: number;
} {
  const pausedRunProof = applyPausedTimeWindowsToRunProof(actions, completedAt, pauseWindows);
  const normalizedClientStartedAt = normalizeTimestamp(clientStartedAt);
  const normalizedServerStartedAt = normalizeTimestamp(serverStartedAt);

  const normalizedActions = pausedRunProof.actions.map((action) => {
    const elapsedSinceLocalStart = action.playedAt - normalizedClientStartedAt;
    if (elapsedSinceLocalStart < 0) {
      throw new Error("Leaderboard timing metadata was invalid.");
    }

    return {
      ...action,
      playedAt: normalizedServerStartedAt + elapsedSinceLocalStart
    };
  });

  const completedElapsedSinceLocalStart =
    pausedRunProof.completedAt - normalizedClientStartedAt;
  if (completedElapsedSinceLocalStart < 0) {
    throw new Error("Leaderboard timing metadata was invalid.");
  }

  return {
    actions: normalizedActions,
    completedAt: normalizedServerStartedAt + completedElapsedSinceLocalStart
  };
}

function mapLeaderboardEntry(row: LeaderboardRow): LeaderboardEntry | null {
  const metricValue = sanitizeMetricValue(row.metric_value);
  if (
    metricValue === null ||
    (typeof row.id !== "string" && typeof row.id !== "number") ||
    typeof row.player_id !== "string" ||
    row.player_id.length === 0 ||
    !isLeaderboardMetricType(row.metric_type) ||
    typeof row.created_at !== "string"
  ) {
    return null;
  }

  return {
    id: String(row.id),
    userId: row.player_id,
    playerName: sanitizeProfileName(row.initials),
    metricType: row.metric_type,
    metricValue,
    createdAt: row.created_at
  };
}

function parseLeaderboardEntries(data: unknown): LeaderboardEntry[] {
  if (!Array.isArray(data)) {
    throw new Error("Leaderboard data was malformed.");
  }

  const parsedEntries = data
    .map((row) => {
      if (typeof row !== "object" || row === null) {
        return null;
      }

      return mapLeaderboardEntry(row as LeaderboardRow);
    })
    .filter((entry): entry is LeaderboardEntry => entry !== null);

  if (data.length > 0 && parsedEntries.length === 0) {
    throw new Error("Leaderboard data was malformed.");
  }

  return parsedEntries;
}

export function parseSubmitLeaderboardRunResult(response: {
  accepted?: unknown;
  entries?: unknown;
  entryIds?: unknown;
  flaggedReason?: unknown;
}): SubmitLeaderboardRunResult {
  const accepted = response.accepted !== false;
  const entries = parseLeaderboardEntries(response.entries ?? []);
  const entryIds = Array.isArray(response.entryIds)
    ? response.entryIds.filter((entryId): entryId is string => typeof entryId === "string")
    : entries.map((entry) => entry.id);
  const flaggedReason =
    typeof response.flaggedReason === "string" && response.flaggedReason.trim().length > 0
      ? response.flaggedReason.trim()
      : null;

  return {
    accepted,
    entries,
    entryIds,
    flaggedReason
  };
}

export function parseStartLeaderboardRunResult(response: {
  runId?: unknown;
  runSeed?: unknown;
  startedAt?: unknown;
}): StartLeaderboardRunResult {
  if (
    typeof response.runId !== "string" ||
    response.runId.trim().length === 0 ||
    typeof response.runSeed !== "string" ||
    response.runSeed.trim().length === 0
  ) {
    throw new Error("Run session response was malformed.");
  }

  return {
    runId: response.runId.trim(),
    runSeed: response.runSeed.trim(),
    startedAt: normalizeTimestamp(response.startedAt)
  };
}

export function getLeaderboardSaveReviewMessage(flaggedReason: string | null): string | null {
  if (flaggedReason === null) {
    return null;
  }

  return "This run was saved for review and may not appear on the public leaderboard yet.";
}

function rewriteBackendErrorMessage(action: "load" | "save", errorMessage?: string): string | undefined {
  if (typeof errorMessage !== "string" || errorMessage.trim().length === 0) {
    return undefined;
  }

  if (action === "save" && isGameRulesVersionMismatchError(errorMessage)) {
    return GAME_RULES_VERSION_MISMATCH_ERROR;
  }

  if (
    action === "save" &&
    errorMessage.includes("leaderboard_entries_initials_check")
  ) {
    return "Database still expects 3-character initials. Apply supabase/migrations/20260324_allow_profile_names.sql.";
  }

  return errorMessage.trim();
}

function createReadableError(action: "load" | "save", errorMessage?: string): Error {
  const baseMessage =
    action === "load" ? "Could not load leaderboard." : "Could not save score.";
  const nextMessage = rewriteBackendErrorMessage(action, errorMessage);

  if (nextMessage !== undefined) {
    return new Error(`${baseMessage} ${nextMessage}`);
  }

  return new Error(baseMessage);
}

async function ensureLeaderboardSession(action: "load" | "save") {
  const client = await requireSupabaseClient();
  const user = await ensureAnonymousSession();
  if (user === null) {
    throw createReadableError(
      action,
      getAnonymousAuthErrorMessage() ?? "Anonymous session unavailable."
    );
  }

  return client;
}

async function requireCurrentUserId(action: "load" | "save"): Promise<string> {
  await requireSupabaseClient();
  const user = await ensureAnonymousSession();
  if (user === null) {
    throw createReadableError(
      action,
      getAnonymousAuthErrorMessage() ?? "Anonymous session unavailable."
    );
  }

  return user.id;
}

function applyPeriodFilter<T extends { gte: (column: string, value: string) => T }>(
  query: T,
  period: LeaderboardPeriod
): T {
  const windowStart = getLeaderboardWindowStart(period);
  if (windowStart === null) {
    return query;
  }

  return query.gte("created_at", windowStart);
}

function applyLeaderboardOrdering<
  T extends { order: (column: string, options?: { ascending?: boolean }) => T }
>(query: T): T {
  return query
    .order("metric_value", { ascending: false })
    .order("created_at", { ascending: true })
    .order("id", { ascending: true });
}

async function fetchHighlightedEntry(
  metricType: LeaderboardMetricType,
  period: LeaderboardPeriod,
  entryId: string
): Promise<RankedLeaderboardEntry | null> {
  const client = await ensureLeaderboardSession("load");
  let entryQuery = client
    .from(LEADERBOARD_TABLE)
    .select(LEADERBOARD_SELECT)
    .eq("metric_type", metricType)
    .eq("id", entryId)
    .limit(1);

  entryQuery = applyPeriodFilter(entryQuery, period);

  const { data, error } = await entryQuery.maybeSingle();
  if (error) {
    throw createReadableError("load", error.message);
  }

  if (data === null) {
    return null;
  }

  const entry = mapLeaderboardEntry(data as LeaderboardRow);
  if (entry === null) {
    return null;
  }

  return rankLeaderboardEntry(entry, period);
}

async function rankLeaderboardEntry(
  entry: LeaderboardEntry,
  period: LeaderboardPeriod
): Promise<RankedLeaderboardEntry> {
  const client = await ensureLeaderboardSession("load");

  let greaterQuery = client
    .from(LEADERBOARD_TABLE)
    .select("id", { count: "exact", head: true })
    .eq("metric_type", entry.metricType)
    .gt("metric_value", entry.metricValue);
  let earlierQuery = client
    .from(LEADERBOARD_TABLE)
    .select("id", { count: "exact", head: true })
    .eq("metric_type", entry.metricType)
    .eq("metric_value", entry.metricValue)
    .lt("created_at", entry.createdAt);
  let tieBreakQuery = client
    .from(LEADERBOARD_TABLE)
    .select("id", { count: "exact", head: true })
    .eq("metric_type", entry.metricType)
    .eq("metric_value", entry.metricValue)
    .eq("created_at", entry.createdAt)
    .lt("id", entry.id);

  greaterQuery = applyPeriodFilter(greaterQuery, period);
  earlierQuery = applyPeriodFilter(earlierQuery, period);
  tieBreakQuery = applyPeriodFilter(tieBreakQuery, period);

  const [
    { count: greaterCount, error: greaterError },
    { count: earlierCount, error: earlierError },
    { count: tieBreakCount, error: tieBreakError }
  ] = await Promise.all([greaterQuery, earlierQuery, tieBreakQuery]);

  const rankError = greaterError ?? earlierError ?? tieBreakError;
  if (rankError) {
    throw createReadableError("load", rankError.message);
  }

  return {
    ...entry,
    rank: (greaterCount ?? 0) + (earlierCount ?? 0) + (tieBreakCount ?? 0) + 1
  };
}

export async function fetchRankedLeaderboardEntry(
  entry: LeaderboardEntry,
  period: LeaderboardPeriod
): Promise<RankedLeaderboardEntry> {
  return rankLeaderboardEntry(entry, period);
}

export async function fetchGlobalLeaderboard(
  metricType: LeaderboardMetricType,
  period: LeaderboardPeriod,
  highlightedEntryId: string | null,
  limit = LEADERBOARD_LIMIT
): Promise<LeaderboardSnapshot> {
  const client = await ensureLeaderboardSession("load");
  logLeaderboardDebug("Loading global leaderboard.", {
    metricType,
    period,
    highlightedEntryId,
    limit
  });

  let topEntriesQuery = client
    .from(LEADERBOARD_TABLE)
    .select(LEADERBOARD_SELECT)
    .eq("metric_type", metricType);
  topEntriesQuery = applyPeriodFilter(topEntriesQuery, period);

  const normalizedLimit = Math.max(1, Math.trunc(limit));
  const topEntriesPromise = applyLeaderboardOrdering(topEntriesQuery).limit(normalizedLimit);

  const highlightedEntryPromise =
    highlightedEntryId === null
      ? Promise.resolve<RankedLeaderboardEntry | null>(null)
      : fetchHighlightedEntry(metricType, period, highlightedEntryId);

  const [{ data, error }, highlightedEntry] = await Promise.all([
    topEntriesPromise,
    highlightedEntryPromise
  ]);

  if (error) {
    throw createReadableError("load", error.message);
  }

  return {
    entries: parseLeaderboardEntries(data),
    highlightedEntry
  };
}

export async function fetchFullGlobalLeaderboard(
  metricType: LeaderboardMetricType,
  period: LeaderboardPeriod,
  pageSize = FULL_LEADERBOARD_PAGE_SIZE
): Promise<LeaderboardEntry[]> {
  const client = await ensureLeaderboardSession("load");
  const normalizedPageSize = Math.max(1, Math.trunc(pageSize));
  const entries: LeaderboardEntry[] = [];
  let start = 0;

  while (true) {
    let pageQuery = client
      .from(LEADERBOARD_TABLE)
      .select(LEADERBOARD_SELECT)
      .eq("metric_type", metricType);
    pageQuery = applyPeriodFilter(pageQuery, period);

    const { data, error } = await applyLeaderboardOrdering(pageQuery).range(
      start,
      start + normalizedPageSize - 1
    );

    if (error) {
      throw createReadableError("load", error.message);
    }

    const pageEntries = parseLeaderboardEntries(data);
    entries.push(...pageEntries);

    if (pageEntries.length < normalizedPageSize) {
      return entries;
    }

    start += normalizedPageSize;
  }
}

export async function submitLeaderboardEntry(
  _entry: SubmitLeaderboardEntryInput
): Promise<LeaderboardEntry> {
  throw new Error("Single-entry leaderboard writes are no longer supported.");
}

function normalizeVerifiedRunAction(action: VerifiedRunAction): VerifiedRunAction {
  return {
    handIndex: normalizeMetricValue(action.handIndex),
    boardIndex: normalizeMetricValue(action.boardIndex),
    playedAt: normalizeTimestamp(action.playedAt)
  };
}

function sanitizeRequestText(value: unknown, maxLength: number): string {
  return typeof value === "string" ? value.trim().slice(0, maxLength) : "";
}

export function normalizeLeaderboardPlayerNameForSave(value: unknown): string {
  const sanitizedValue = sanitizeProfileName(value);
  if (sanitizedValue.length === 0) {
    return DEFAULT_LEADERBOARD_PLAYER_NAME;
  }

  return sanitizeProfileNameForSave(sanitizedValue, { requireNonBlank: true });
}

export async function startLeaderboardRun(
  input: StartLeaderboardRunInput
): Promise<StartLeaderboardRunResult> {
  await ensureLeaderboardSession("save");

  let response: {
    runId?: unknown;
    runSeed?: unknown;
    startedAt?: unknown;
  };

  try {
    response = await invokeSupabaseFunction("start-run", {
      appVersion: sanitizeRequestText(input.appVersion, 64),
      gameRulesVersion: sanitizeGameRulesVersion(input.gameRulesVersion) || GAME_RULES_VERSION,
      guidedRun: input.guidedRun === true
    });
  } catch (error) {
    throw createReadableError("save", error instanceof Error ? error.message : undefined);
  }

  return parseStartLeaderboardRunResult(response);
}

export async function submitLeaderboardRun(
  submission: SubmitLeaderboardRunInput
): Promise<SubmitLeaderboardRunResult> {
  const userId = await requireCurrentUserId("save");
  const playerName = normalizeLeaderboardPlayerNameForSave(submission.playerName);
  if (playerName.length === 0) {
    throw createReadableError(
      "save",
      getProfileNameValidationError(submission.playerName, { requireNonBlank: true }) ??
        "Set a valid profile name before saving."
    );
  }

  logLeaderboardDebug("Submitting verified run proof.", {
    userId,
    runId: submission.runId,
    actionCount: submission.actions.length
  });

  let response: {
    accepted?: unknown;
    entries?: unknown;
    entryIds?: unknown;
    flaggedReason?: unknown;
  };

  try {
    response = await invokeSupabaseFunction<{
      accepted?: unknown;
      entries?: unknown;
      entryIds?: unknown;
      flaggedReason?: unknown;
    }>("submit-run", {
      actions: submission.actions.map(normalizeVerifiedRunAction),
      appVersion: sanitizeRequestText(submission.appVersion, 64),
      completedAt: normalizeTimestamp(submission.completedAt),
      gameRulesVersion:
        sanitizeGameRulesVersion(submission.gameRulesVersion) || GAME_RULES_VERSION,
      playerName,
      runId: sanitizeRequestText(submission.runId, 80)
    });
  } catch (error) {
    throw createReadableError("save", error instanceof Error ? error.message : undefined);
  }

  const result = parseSubmitLeaderboardRunResult(response);

  if (!result.accepted) {
    throw createReadableError("save", result.flaggedReason ?? "Run was not accepted.");
  }

  return result;
}
